
import type { Article } from '../../../mocks/blog';

interface ArticleListProps {
  articles: Article[];
  onEdit: (article: Article) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string) => void;
  onNew: () => void;
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

export default function ArticleList({ articles, onEdit, onDelete, onToggleStatus, onNew }: ArticleListProps) {
  const published = articles.filter((a) => a.status === 'published').length;
  const drafts = articles.filter((a) => a.status === 'draft').length;

  return (
    <div className="space-y-6">
      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: '全部文章', value: articles.length, icon: 'ri-article-line', color: 'text-amber-400' },
          { label: '已發布', value: published, icon: 'ri-checkbox-circle-line', color: 'text-emerald-400' },
          { label: '草稿', value: drafts, icon: 'ri-draft-line', color: 'text-white/40' },
        ].map(({ label, value, icon, color }) => (
          <div key={label} className="bg-white/[0.03] border border-amber-500/10 rounded-sm p-5 flex items-center gap-4">
            <div className={`w-10 h-10 flex items-center justify-center rounded-sm bg-white/[0.04] ${color}`}>
              <i className={`${icon} text-lg`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-white/90" style={{ fontFamily: "'Noto Serif TC', serif" }}>{value}</p>
              <p className="text-white/30 text-xs tracking-wider" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Table header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-4 h-px bg-amber-500/50" />
          <span className="text-white/40 text-xs tracking-[0.3em]" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
            文章列表
          </span>
        </div>
        <button
          onClick={onNew}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-yellow-400 rounded-sm text-black text-xs font-bold tracking-widest hover:shadow-lg hover:shadow-amber-500/40 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
        >
          <i className="ri-add-line text-sm" />
          新增文章
        </button>
      </div>

      {/* Article rows */}
      {articles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 bg-white/[0.02] border border-amber-500/10 rounded-sm">
          <i className="ri-file-add-line text-4xl text-amber-500/20 mb-4" />
          <p className="text-white/30 text-sm tracking-wider mb-6" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
            還沒有任何文章，立即新增第一篇吧！
          </p>
          <button
            onClick={onNew}
            className="px-6 py-2.5 border border-amber-500/30 text-amber-400 text-xs tracking-wider rounded-sm hover:bg-amber-500/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            新增文章
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {articles.map((article) => (
            <div
              key={article.id}
              className="group flex items-center gap-5 bg-white/[0.02] border border-amber-500/10 rounded-sm p-4 hover:border-amber-500/25 transition-all duration-300"
            >
              {/* Cover thumbnail */}
              <div className="w-20 h-14 shrink-0 overflow-hidden rounded-sm">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span
                    className={`px-2 py-0.5 text-xs rounded-sm border ${
                      article.status === 'published'
                        ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-400'
                        : 'bg-white/[0.04] border-white/10 text-white/30'
                    }`}
                    style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                  >
                    {article.status === 'published' ? '已發布' : '草稿'}
                  </span>
                  <span className="text-white/25 text-xs tracking-wider" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
                    {formatDate(article.date)}
                  </span>
                </div>
                <h3
                  className="text-white/80 text-sm font-semibold truncate mb-1.5 group-hover:text-amber-400/90 transition-colors duration-300"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  {article.title}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {article.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 bg-amber-500/10 border border-amber-500/15 text-amber-400/60 text-xs rounded-sm"
                      style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                    >
                      {tag}
                    </span>
                  ))}
                  {article.tags.length > 3 && (
                    <span className="text-white/20 text-xs" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
                      +{article.tags.length - 3}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {/* Toggle status */}
                <button
                  onClick={() => onToggleStatus(article.id)}
                  title={article.status === 'published' ? '設為草稿' : '發布文章'}
                  className={`w-8 h-8 flex items-center justify-center rounded-sm border transition-all duration-200 cursor-pointer ${
                    article.status === 'published'
                      ? 'border-white/10 text-white/30 hover:border-amber-400/30 hover:text-amber-400/70'
                      : 'border-emerald-500/20 text-emerald-400/50 hover:border-emerald-400/40 hover:text-emerald-400'
                  }`}
                >
                  <i className={`text-sm ${article.status === 'published' ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
                </button>

                {/* Edit */}
                <button
                  onClick={() => onEdit(article)}
                  title="編輯文章"
                  className="w-8 h-8 flex items-center justify-center rounded-sm border border-amber-500/15 text-amber-400/50 hover:border-amber-400/40 hover:text-amber-400 transition-all duration-200 cursor-pointer"
                >
                  <i className="ri-edit-line text-sm" />
                </button>

                {/* Delete */}
                <button
                  onClick={() => onDelete(article.id)}
                  title="刪除文章"
                  className="w-8 h-8 flex items-center justify-center rounded-sm border border-red-500/15 text-red-400/40 hover:border-red-400/40 hover:text-red-400 transition-all duration-200 cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
